<div
  class="page min-h-screen flex bg-white font-sans text-gray-900 selection:bg-orange-100 selection:text-brand"
>
  <!-- Left Side - Form Section -->
  <div
    class="w-full lg:w-1/2 flex flex-col justify-center px-4 sm:px-12 lg:px-20 xl:px-24 py-12 relative z-10 bg-white"
  >
    <div class="max-w-md w-full mx-auto">
      <!-- Back Button (funnel mode only) -->
      @if (isFunnelMode) {
        <button
          type="button"
          (click)="goBack()"
          class="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium mb-8 group cursor-pointer"
        >
          <lucide-icon
            [img]="icons.ArrowLeft"
            class="w-5 h-5 group-hover:-translate-x-1 transition-transform"
          ></lucide-icon>
          Go back
        </button>
      }

      <!-- Logo -->
      <a
        routerLink="/wizard/v2/landing"
        class="flex justify-center mb-8 hover:opacity-80 transition-opacity"
      >
        <img src="images/logo.png" alt="JustTeach Logo" class="h-8 w-auto" />
      </a>

      <div class="mb-10 text-center">
        @if (isFunnelMode) {
          <h1 class="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-3">Welcome back!</h1>
          <p class="text-gray-600">Sign in to continue where you left off.</p>
        } @else {
          <h1 class="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-3">Welcome back</h1>
          <p class="text-gray-600">
            Sign in to continue with JustTeach.
          </p>
        }
      </div>

      <!-- Error Message -->
      @if (errorMessage$ | async; as errorMsg) {
        <div
          class="flex items-center justify-center gap-2 bg-red-50 text-red-600 px-4 py-3 rounded-xl mb-6 text-sm font-medium"
        >
          <svg
            class="w-5 h-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          {{ errorMsg }}
        </div>
      }

      <!-- Form -->
      <form [formGroup]="loginForm" (ngSubmit)="onSubmit()" class="space-y-5">
        <!-- Email -->
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2">Email</label>
          <div class="relative">
            <div
              class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400"
            >
              <lucide-icon [img]="icons.Mail" class="w-5 h-5"></lucide-icon>
            </div>
            <input
              type="email"
              formControlName="email"
              class="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none transition-all font-medium"
              [class.border-red-400]="
                loginForm.get('email')?.invalid && loginForm.get('email')?.touched
              "
              [class.focus:border-red-400]="
                loginForm.get('email')?.invalid && loginForm.get('email')?.touched
              "
              [class.focus:ring-red-400/20]="
                loginForm.get('email')?.invalid && loginForm.get('email')?.touched
              "
              placeholder="name@school.edu"
              autocomplete="email"
            />
          </div>
          @if (loginForm.get('email')?.invalid && loginForm.get('email')?.touched) {
            <p class="mt-2 text-sm text-red-500 font-medium">Please enter a valid email address</p>
          }
        </div>

        <!-- Password -->
        <div>
          <div class="flex justify-between items-center mb-2">
            <label class="block text-sm font-semibold text-gray-700">Password</label>
            <button
              type="button"
              (click)="openForgotPasswordModal()"
              class="text-sm text-brand font-medium hover:underline cursor-pointer"
            >
              Forgot password?
            </button>
          </div>
          <div class="relative">
            <div
              class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400"
            >
              <lucide-icon [img]="icons.Lock" class="w-5 h-5"></lucide-icon>
            </div>
            <input
              [type]="showPassword() ? 'text' : 'password'"
              formControlName="password"
              class="w-full pl-10 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none transition-all font-medium"
              [class.border-red-400]="
                loginForm.get('password')?.invalid && loginForm.get('password')?.touched
              "
              [class.focus:border-red-400]="
                loginForm.get('password')?.invalid && loginForm.get('password')?.touched
              "
              [class.focus:ring-red-400/20]="
                loginForm.get('password')?.invalid && loginForm.get('password')?.touched
              "
              placeholder="Enter your password"
              autocomplete="current-password"
            />
            <button
              type="button"
              (click)="togglePasswordVisibility()"
              class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              @if (showPassword()) {
                <lucide-icon [img]="icons.EyeOff" class="w-5 h-5"></lucide-icon>
              } @else {
                <lucide-icon [img]="icons.Eye" class="w-5 h-5"></lucide-icon>
              }
            </button>
          </div>
          @if (loginForm.get('password')?.invalid && loginForm.get('password')?.touched) {
            <p class="mt-2 text-sm text-red-500 font-medium">
              Password must be at least 8 characters
            </p>
          }
        </div>

        <button
          type="submit"
          class="w-full bg-brand hover:bg-brand-dark text-white font-bold py-4 rounded-xl transition-all shadow-lg shadow-brand/20 flex items-center justify-center gap-2 group disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
          [disabled]="isLoading$ | async"
        >
          @if (isLoading$ | async) {
            <svg
              class="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Signing in...
          } @else {
            @if (isFunnelMode) {
              Sign In & Continue
            } @else {
              Sign In
            }
            <lucide-icon
              [img]="icons.ArrowRight"
              class="w-5 h-5 group-hover:translate-x-1 transition-transform"
            ></lucide-icon>
          }
        </button>
      </form>

      <!-- Divider -->
      <div class="relative flex py-2 items-center my-8">
        <div class="flex-grow border-t border-gray-200"></div>
        <span class="flex-shrink-0 mx-4 text-gray-400 text-sm font-medium">Or continue with</span>
        <div class="flex-grow border-t border-gray-200"></div>
      </div>

      <!-- OAuth Buttons -->
      <div class="space-y-3 mb-8">
        <button
          type="button"
          (click)="signInWithGoogle()"
          class="w-full flex items-center justify-center gap-3 bg-white border border-gray-200 text-gray-700 font-medium p-3 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 cursor-pointer"
        >
          <svg class="w-5 h-5" viewBox="0 0 24 24">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
          Sign in with Google
        </button>

        <button
          type="button"
          (click)="signInWithMicrosoft()"
          class="w-full flex items-center justify-center gap-3 bg-white border border-gray-200 text-gray-700 font-medium p-3 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 cursor-pointer"
        >
          <svg class="w-5 h-5" viewBox="0 0 23 23">
            <path fill="#f35325" d="M1 1h10v10H1z" />
            <path fill="#81bc06" d="M12 1h10v10H12z" />
            <path fill="#05a6f0" d="M1 12h10v10H1z" />
            <path fill="#ffba08" d="M12 12h10v10H12z" />
          </svg>
          Sign in with Microsoft
        </button>
      </div>

      <!-- Sign Up Link -->
      <div class="text-center">
        <p class="text-gray-600 font-medium">
          Don't have an account?
          <a [routerLink]="['/sign-up']" [queryParams]="forwardQueryParams" class="text-brand font-bold hover:underline">Sign up</a>
        </p>
      </div>
    </div>
  </div>

  <!-- Right Side - Visual Section (Hidden on Mobile) -->
  <div
    class="hidden lg:flex lg:w-1/2 bg-orange-50 relative overflow-hidden items-center justify-center p-12"
  >
    <!-- Background Blobs -->
    <div
      class="absolute top-20 right-20 w-72 h-72 bg-orange-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob"
    ></div>
    <div
      class="absolute bottom-20 left-20 w-72 h-72 bg-yellow-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob animation-delay-2000"
    ></div>

    <!-- Testimonial / Value Card -->
    <div class="relative max-w-md w-full">
      <!-- Abstract Decorative Elements -->
      <div class="absolute -top-12 -left-12 text-brand/10">
        <svg width="100" height="100" viewBox="0 0 100 100" fill="currentColor">
          <pattern id="grid" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="2" />
          </pattern>
          <rect width="100" height="100" fill="url(#grid)" />
        </svg>
      </div>

      <div
        class="bg-white/80 backdrop-blur-xl border border-white/50 rounded-3xl p-8 shadow-2xl shadow-orange-500/10 relative"
      >
        <!-- Stars -->
        <div class="flex gap-1 mb-6">
          @for (i of [1, 2, 3, 4, 5]; track i) {
            <lucide-icon
              [img]="icons.Star"
              class="w-5 h-5 text-yellow-400 fill-yellow-400"
            ></lucide-icon>
          }
        </div>

        <p class="text-xl font-medium text-gray-800 leading-relaxed mb-6 font-serif italic">
          "The lesson plans are incredibly detailed and ready to use. I've saved countless hours every week since I started using JustTeach."
        </p>

        <div class="flex items-center gap-4">
          <div
            class="w-12 h-12 rounded-full bg-brand flex items-center justify-center text-white font-bold text-lg shadow-md shadow-brand/20"
          >
            <img class="rounded-full" [src]="'/images/landing/teacher1.png'" />
          </div>
          <div>
            <h4 class="font-bold text-gray-900">Mark Thompson</h4>
            <p class="text-sm text-gray-500">5th Grade Science</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Text -->
    <div class="absolute bottom-8 text-center text-gray-500 text-xs font-medium max-w-xs">
      Join 10,000+ teachers saving time with JustTeach
    </div>
  </div>
</div>

<!-- Forgot Password Modal -->
<app-forgot-password-modal
  [isOpen]="showForgotPasswordModal()"
  (closed)="closeForgotPasswordModal()"
></app-forgot-password-modal>

